import { request } from './apiClient';
import { getSessionClientId } from './authSession';

// Normaliza o payload para o contrato do backend de resultado de corpo de prova.
// Regras:
// - campos numericos chegam como number;
// - string vazia vira null para distinguir "nao informado" de zero;
// - aceita virgula no decimal digitado pela UI.
function parseNullableDecimal(value) {
    if (value === undefined || value === null || value === '') return null;
    const normalized = Number(String(value).replace(',', '.'));
    return Number.isFinite(normalized) ? normalized : null;
}

// Converte para inteiro, ou null se vazio/não inteiro.
function parseNullableInt(value) {
    if (value === undefined || value === null || value === '') return null;
    const normalized = Number(value);
    return Number.isInteger(normalized) ? normalized : null;
}

// Converte data para datetime (acrescenta T00:00:00 a datas puras); null se vazio.
function toNullableDateTime(value) {
    if (!value) return null;
    const asString = String(value);
    // AJUSTE BACKEND AQUI: se a API exigir outro formato de datetime, adapte esta conversao.
    if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return `${asString}T00:00:00`;
    return asString;
}

// Monta o payload de resultado de CP (numéricos como number, vazios como null).
// AJUSTE BACKEND AQUI: este mapeamento segue dbo.resultado_corpo_prova.
// Se o contrato da API mudar (snake_case/camelCase ou nomes diferentes), ajuste somente este bloco.
export const buildResultadoCorpoProvaPayload = (formData) => ({
    idCliente: getSessionClientId(),
    idCorpoProva: parseNullableInt(formData.idCorpoProva),
    valrCarga: parseNullableDecimal(formData.valrCarga),
    idTipoRuptura: parseNullableInt(formData.idTipoRuptura),
    observacao: formData.observacao ? String(formData.observacao).trim() : null,
    valrA: parseNullableDecimal(formData.valrA),
    idPrensa: parseNullableInt(formData.idPrensa),
    dataRompimento: toNullableDateTime(formData.dataRompimento),
});

// Lista todos os resultados de corpos de prova.
export const getResultadoCorpoProva = async (idCliente) => {
    // AJUSTE BACKEND AQUI: endpoint de listagem da pagina de resultados.
    const effectiveClientId = idCliente || getSessionClientId();
    return await request(`/api/resultadoCorpoProva?idCliente=${effectiveClientId}`);
};

// Cria um resultado de CP; retorna { response, payload }.
export const createResultadoCorpoProva = async (resultadoCorpoProvaData) => {
    const payload = buildResultadoCorpoProvaPayload(resultadoCorpoProvaData);
    // AJUSTE BACKEND AQUI: endpoint de criacao de resultado.
    const response = await request("/api/resultadoCorpoProva", {
        method: 'POST',
        body: JSON.stringify(payload)
    });

    return { response, payload };
};

// Atualiza um resultado de CP por id (PK id_resu_corpo_prova); retorna { response, payload }.
export const updateResultadoCorpoProva = async (idResultadoCorpoProva, resultadoCorpoProvaData) => {
    const payload = buildResultadoCorpoProvaPayload(resultadoCorpoProvaData);
    payload.idResuCorpoProva = Number(idResultadoCorpoProva)
    // AJUSTE BACKEND AQUI: este id precisa ser o PK do resultado (id_resu_corpo_prova).
    const response = await request(`/api/resultadoCorpoProva/${idResultadoCorpoProva}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    });

    return { response, payload };
};
// Exclui um resultado de CP por id.
export const deleteResultadoCorpoProva = async (idResultadoCorpoProva) => {
    // AJUSTE BACKEND AQUI: exclusao por PK do resultado (id_resu_corpo_prova).
    return await request(`/api/resultadoCorpoProva/${idResultadoCorpoProva}`, {
        method: 'DELETE'
    })
}

// Revisão de resultados: caminhões da concretagem (um por nota fiscal), cada um com todos os
// seus corpos de prova e o resultado mais recente de cada CP (null quando pendente).
export const getRevisaoResultado = async (idConcretagem) => {
    return await request(`/api/resultadoCorpoProva/revisao?idConcretagem=${idConcretagem}`);
};

// Monta o payload da revisão: a concretagem e uma entrada por resultado com a carga corrigida.
export const buildRevisaoResultadoPayload = (idConcretagem, itens) => ({
    idConcretagemRealizada: parseNullableInt(idConcretagem),
    itens: itens.map((item) => ({
        idResuCorpoProva: parseNullableInt(item.idResultado),
        valrCargaCorrigida: parseNullableDecimal(item.cargaCorrigida),
    })),
});

// Salva as cargas corrigidas de uma vez (null remove a correção); retorna { response, payload }.
export const salvarRevisaoResultado = async (idConcretagem, itens) => {
    const payload = buildRevisaoResultadoPayload(idConcretagem, itens);
    const response = await request('/api/resultadoCorpoProva/revisao', {
        method: 'PUT',
        body: JSON.stringify(payload)
    });

    return { response, payload };
};
